/**
 * FeedbackButton — top-bar entry point for the in-app feedback loop (#17c).
 *
 * Present on every route (mounted in the AppShell header), so it covers both
 * the analytics pages and the ops pages that render under the same shell.
 *
 * Flow: clicking the button opens a small modal IMMEDIATELY — a notepad
 * textarea + kind selector, no async work before it mounts. On submit it
 * POSTs the text + page context (URL, react-router route, viewport, user
 * agent) to /api/feedback. After a successful submit, the modal offers
 * "Send another note" so a run of quick notes doesn't require reopening it.
 *
 * There used to be a screenshot capture step here (html2canvas over
 * document.body) that ran BEFORE the modal opened, blocking the main thread
 * for a full-page rasterize. It was removed: html2canvas 1.4.1 is unmaintained
 * and its color parser throws on the oklab()/color-mix() values Tailwind v4
 * emits for opacity-modifier utilities (ring-accent/20, bg-accent-soft/*, ~90
 * uses across the app), so the capture failed on every route — 0 of the first
 * 15 feedback rows ever got a screenshot. Two months of production use with
 * text + route + viewport shows that's enough context without paying a
 * multi-second freeze for a feature with a 0% success rate.
 *
 * Token-pure styling on the v2 design system (Button/Input/Select from ui.tsx
 * + semantic tokens), so it's correct in light + dark and needs no Phase 5
 * re-skin.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Check, Loader2, MessageSquarePlus, X } from 'lucide-react'
import { Button, Select, inputClass } from './ui'
import { feedbackApi, type FeedbackKind } from '../lib/feedbackApi'

export default function FeedbackButton() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState('')
  const [kind, setKind] = useState<FeedbackKind>('feedback')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  // Escape closes (unless a request is in flight). Matches the app's dialogs.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busy, close])

  // Focus the textarea once the modal is open, and again each time "Send
  // another note" flips done back to false.
  useEffect(() => {
    if (open && !done) textareaRef.current?.focus()
  }, [open, done])

  const onOpen = () => {
    if (open) return
    setError(null); setDone(false); setBody(''); setKind('feedback')
    setOpen(true)
  }

  const onSendAnother = () => {
    setDone(false); setBody(''); setKind('feedback'); setError(null)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    const text = body.trim()
    if (!text) { setError('Please describe the feedback.'); return }
    setBusy(true); setError(null)
    try {
      await feedbackApi.submit({
        body: text,
        kind,
        page_url: window.location.href,
        route: location.pathname,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        user_agent: navigator.userAgent,
      })
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpen}
        title="Send feedback or report a bug"
        aria-label="Send feedback"
      >
        <MessageSquarePlus size={14} />
        <span className="hidden sm:inline">Feedback</span>
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !busy && close()} />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Send feedback"
            className="relative w-[460px] max-w-[95vw] bg-surface text-ink rounded-xl border border-edge shadow-card flex flex-col"
          >
            <header className="px-4 py-3 border-b border-edge-subtle flex items-center justify-between">
              <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
                <MessageSquarePlus size={15} className="text-ink-faint" /> Send feedback
              </h2>
              <button
                type="button"
                onClick={() => !busy && close()}
                disabled={busy}
                aria-label="Close"
                className="text-ink-muted hover:text-ink px-1 disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </header>

            <form onSubmit={onSubmit} className="p-4 space-y-3">
              {done ? (
                <>
                  <div className="flex items-center gap-2 rounded-lg border border-success-edge bg-success-soft px-3 py-2 text-sm text-success-soft-fg">
                    <Check size={16} className="shrink-0" /> Thanks — your feedback was sent.
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" variant="ghost" size="sm" onClick={close}>
                      Close
                    </Button>
                    <Button type="button" variant="primary" size="sm" onClick={onSendAnother}>
                      <MessageSquarePlus size={14} />
                      Send another note
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {error && (
                    <div role="alert" className="rounded-lg border border-danger-edge bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
                      {error}
                    </div>
                  )}
                  <label className="block">
                    <span className="block text-xs font-medium text-ink-secondary mb-1">Type</span>
                    <Select value={kind} onChange={e => setKind(e.target.value as FeedbackKind)} className="w-full">
                      <option value="feedback">General feedback</option>
                      <option value="bug">Bug report</option>
                      <option value="feature">Feature request</option>
                    </Select>
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-ink-secondary mb-1">Details</span>
                    <textarea
                      ref={textareaRef}
                      required
                      rows={5}
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      placeholder="What's working, what's not, or what you'd like to see…"
                      className={`${inputClass} w-full h-auto py-2 resize-y`}
                    />
                  </label>
                  <p className="text-[11px] text-ink-faint">
                    Your note is sent with the page you are on: <span className="font-mono">{location.pathname}</span>
                  </p>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" variant="ghost" size="sm" onClick={close} disabled={busy}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary" size="sm" disabled={busy || !body.trim()}>
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <MessageSquarePlus size={14} />}
                      Send
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  )
}
